import { Alert, RadioInput } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  PromoterConditionRule,
  PromoterConditionRuleOperator,
  PromoterConditionRuleOperatorCaptions,
  PromoterConditions,
  PromoterModel,
} from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import validator from 'validator';
import ErrorMessage from '../../../../../../../common/components/ErrorMessage/ErrorMessage';
import SelectorWithArrow, {
  itemsFromObject,
} from '../../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import TextInput from '../../../../../../../common/components/TextInput';
import { useCustomerChatConditionsEditorPromoterState } from '../use-customer-chat-conditions-editor-state';

interface Props {
  usedInSlideout?: boolean;
  warnAboutMultipleRuleFreePromoters?: boolean;
  duplicateRules?: PromoterConditionRule[];
  localPromoter: ReturnType<
    typeof useCustomerChatConditionsEditorPromoterState
  >['localPromoter'];
  setLocalPromoter: ReturnType<
    typeof useCustomerChatConditionsEditorPromoterState
  >['setLocalPromoter'];
  updatePromoter: (updatedPromoter: PromoterModel) => void;
  updateLocalPromoterField: <K extends keyof PromoterModel>(
    key: K,
    value: PromoterModel[K],
  ) => void;
}

const RuleErrorTypes = {
  error: 1,
  duplicateRule: 2,
} as const;
type RuleError =
  | { type: typeof RuleErrorTypes.error; reason: string }
  | { type: typeof RuleErrorTypes.duplicateRule };

const DuplicateRuleWarning = () => (
  <Alert
    type="warning"
    showCloseIcon={false}
    className="customer-chat-conditions-editor__rule-duplicate-error"
  >
    <div>
      <div className="customer-chat-conditions-editor__rule-duplicate-error-heading">
        Another automation already exists for this targeting URL
      </div>
      <div>Please create a more specific targeting URL for your automation</div>
    </div>
  </Alert>
);

export const CustomerChatConditionRulesEditor: FunctionComponent<
  Props
> = props => {
  const {
    usedInSlideout,
    warnAboutMultipleRuleFreePromoters,
    duplicateRules = [],
    updatePromoter,
    updateLocalPromoterField,
    localPromoter,
    setLocalPromoter,
  } = props;
  const canEditConditions = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatEditConditions;
  const modalActions = useModalActions();

  if (!localPromoter) {
    return null;
  }

  const addNewRule = () => {
    const oldRules = localPromoter.conditions.rules;
    const newOperator =
      oldRules.length === 0 ? 'is' : oldRules[oldRules.length - 1].operator;
    const updatedConditions: PromoterConditions = {
      ...localPromoter.conditions,
      rules: [...oldRules, { operator: newOperator, value: '' }],
    };
    const updatedPromoter = { ...localPromoter, conditions: updatedConditions };
    setLocalPromoter(updatedPromoter);
  };

  const removeRule = (index: number) => {
    const updatedConditions: PromoterConditions = {
      ...localPromoter.conditions,
      rules: [...localPromoter.conditions.rules],
    };
    updatedConditions.rules.splice(index, 1);
    const updatedPromoter = { ...localPromoter, conditions: updatedConditions };
    setLocalPromoter(updatedPromoter);
    updatePromoter(updatedPromoter);
  };

  const ruleError = (rule: PromoterConditionRule): RuleError | undefined => {
    switch (rule.operator) {
      case '*':
        break;
      case 'is':
      case 'is_not':
        if (!rule.value) {
          return {
            type: RuleErrorTypes.error,
            reason: 'This field cannot be empty',
          };
        }
        if (
          rule.value.search('://') > 0 ||
          !validator.isURL(rule.value, {
            require_protocol: false,
            protocols: ['http', 'https'],
          })
        ) {
          return {
            type: RuleErrorTypes.error,
            reason:
              'Enter valid URL without scheme (e.g., mobilemonkey.com/prices)',
          };
        }
        break;
      default:
        if (!rule.value) {
          return {
            type: RuleErrorTypes.error,
            reason: 'This field cannot be empty',
          };
        }
    }
    if (
      duplicateRules.some(
        r => r.value === rule.value && r.operator === rule.operator,
      )
    ) {
      return { type: RuleErrorTypes.duplicateRule };
    }
  };

  const rulesAndRuleErrors = localPromoter.conditions.rules.map(rule => ({
    rule,
    ruleError: ruleError(rule),
  }));

  const updateRule = <K extends keyof PromoterConditionRule>(
    index: number,
    key: K,
    value: PromoterConditionRule[K],
    persist: boolean = true,
  ) => {
    const updatedConditions: PromoterConditions = {
      ...localPromoter.conditions,
      rules: [...localPromoter.conditions.rules],
    };
    const updatedRule = {
      ...updatedConditions.rules[index],
      [key]: value,
    };
    updatedConditions.rules[index] = updatedRule;
    const updatedPromoter = { ...localPromoter, conditions: updatedConditions };
    setLocalPromoter(updatedPromoter);
    if (persist && !ruleError(updatedRule)) {
      updatePromoter(updatedPromoter);
    }
  };

  const onChangeOperator = (idx: number) => (k: any) => {
    if (!canEditConditions) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames[
            'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__CHANGE-OPERATOR'
          ],
        feature: 'customerChatEditConditions',
      });
      return;
    }

    updateRule(idx, 'operator', k as PromoterConditionRuleOperator);
  };

  const onAddNewCondition = () => {
    if (!canEditConditions) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames[
            'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__CONDITIONS-EDITOR__ADD-NEW-RULE'
          ],
        feature: 'customerChatEditConditions',
      });
      return;
    }
    addNewRule();
  };

  const showAlert =
    localPromoter.conditions.rules.filter(rule =>
      ['is', 'starts_with', 'ends_with'].includes(rule.operator),
    ).length >= 2;

  return (
    <>
      {' '}
      <div
        className={
          usedInSlideout
            ? 'customer-chat-conditions-editor__rule-mode--slideout'
            : 'customer-chat-conditions-editor__rule-mode'
        }
      >
        <span className="customer-chat-conditions-editor__rule-mode-caption">
          Show when website visitor meets:
        </span>
        <RadioInput
          className="customer-chat-conditions-editor__rule-mode-radio"
          name="ruleMode"
          checked={localPromoter.conditions.ruleMode === 'any'}
          value="any"
          label="At least one condition"
          onChange={() =>
            updateLocalPromoterField('conditions', {
              ...localPromoter.conditions,
              ruleMode: 'any',
            })
          }
        />
        <RadioInput
          name="ruleMode"
          checked={localPromoter.conditions.ruleMode === 'all'}
          value="all"
          label="Every condition"
          onChange={() =>
            updateLocalPromoterField('conditions', {
              ...localPromoter.conditions,
              ruleMode: 'all',
            })
          }
        />
        {localPromoter.conditions.ruleMode === 'all' &&
          localPromoter.conditions.rules.length > 1 &&
          showAlert && (
            <Alert
              className="customer-chat-conditions__alert-icon"
              type="error"
              showCloseIcon={false}
            >
              This combination of rules will result in the chat widget not
              appearing on any page
            </Alert>
          )}
      </div>
      <div className="customer-chat-conditions-editor__rules">
        {rulesAndRuleErrors.map(({ rule, ruleError }, idx) => (
          <div
            className={
              usedInSlideout
                ? 'customer-chat-conditions-editor__rule--slideout'
                : 'customer-chat-conditions-editor__rule'
            }
            key={idx}
          >
            <span className="customer-chat-conditions-editor__rule-variable">
              Current URL
              {localPromoter.conditions.rules.length > 1 && usedInSlideout && (
                <i
                  className="far fa-trash-alt customer-chat-conditions-editor__rule-trash"
                  onClick={() => removeRule(idx)}
                />
              )}
            </span>
            <SelectorWithArrow
              selectorClassName={classNames(
                'customer-chat-conditions-editor__rule-operator',
                {
                  'customer-chat-conditions-editor__rule-operator--slideout':
                    usedInSlideout,
                },
              )}
              activeSelectorClassName="customer-chat-conditions-editor__rule-operator-active-selector--slideout"
              selectorDropdownClassName="customer-chat-conditions-editor__rule-operator-selector-dropdown--slideout"
              items={itemsFromObject(PromoterConditionRuleOperatorCaptions)}
              defaultLabel={
                PromoterConditionRuleOperatorCaptions[rule.operator]
              }
              onChange={onChangeOperator(idx)}
            />
            {rule.operator !== '*' ? (
              <>
                {!usedInSlideout && (
                  <i className="fas fa-arrow-right customer-chat-conditions-editor__rule-arrow" />
                )}
                <div className="customer-chat-conditions-editor__rule-value-wrapper">
                  <TextInput
                    className={
                      usedInSlideout
                        ? 'customer-chat-conditions-editor__rule-value--slideout'
                        : 'customer-chat-conditions-editor__rule-value'
                    }
                    placeholder={
                      rule.operator === 'is' || rule.operator === 'is_not'
                        ? 'Enter URL without scheme (e.g., mobilemonkey.com/prices)'
                        : 'Enter URL'
                    }
                    style={
                      usedInSlideout ? { border: '1px solid #d9d9d9' } : {}
                    }
                    value={rule.value}
                    onChange={e =>
                      updateRule(idx, 'value', e.target.value, false)
                    }
                    onBlur={() =>
                      ruleError?.type !== RuleErrorTypes.error &&
                      updatePromoter(localPromoter)
                    }
                    error={ruleError?.type === RuleErrorTypes.error}
                  />

                  {ruleError?.type === RuleErrorTypes.error && (
                    <ErrorMessage
                      displayType="triangle"
                      className="customer-chat-conditions-editor__rule-value-error"
                      error={ruleError.reason}
                    />
                  )}

                  {ruleError?.type === RuleErrorTypes.duplicateRule && (
                    <DuplicateRuleWarning />
                  )}
                </div>
              </>
            ) : (
              !usedInSlideout && (
                <div className="customer-chat-conditions-editor__rule-spacer">
                  &nbsp;
                </div>
              )
            )}

            {localPromoter.conditions.rules.length > 1 && !usedInSlideout && (
              <i
                className="far fa-trash-alt customer-chat-conditions-editor__rule-trash"
                onClick={() => removeRule(idx)}
              />
            )}
          </div>
        ))}
        {warnAboutMultipleRuleFreePromoters && <DuplicateRuleWarning />}
        <div
          className={classNames(
            'customer-chat-conditions-editor__add-rule-button',
            {
              'customer-chat-conditions-editor__add-rule-button--slideout':
                usedInSlideout,
            },
          )}
          onClick={onAddNewCondition}
        >
          + Add new condition
        </div>
      </div>
    </>
  );
};
